import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { searchV1, understandSearch, type SearchV1Group } from '@/lib/searchApi';
import { parseQuery, type ParsedQuery } from '@/lib/search/parseQuery';
import {
  EMPTY_OVERRIDES, buildChips, effectiveFilters, hasFilters, localToday, pruneOverrides, toSearchParams,
  type Chip, type ChipFilters, type FilterOverrides,
} from '@/lib/search/searchFilterChips';
import { mergeUnderstanding, type MergedQuery, type UnderstandV1 } from '@/lib/search/understandMerge';
import type { SearchParseContext } from '@/hooks/useSearchParseContext';

export const RECENT_KEY = 'ht:search:recent';
const DEBOUNCE_MS = 100;
const MAX_RECENT = 8;
/** T-151: ask for an AI reading after this pause, when 2+ words are left unread. */
const UNDERSTAND_DEBOUNCE_MS = 400;
// A reading that hasn't answered by then is given up: the text keeps the parser's reading,
// so the list never says "Searching…" forever. The server's own provider calls stop sooner.
const UNDERSTAND_CLIENT_TIMEOUT_MS = 10_000;
const MAX_UNDERSTAND_QUERY = 200;
/** Readings kept per text (and local day), so going back to a text doesn't ask again. */
const MAX_READINGS = 20;
/** With an entity chip only that group shows, so it gets the server's max page. */
const ENTITY_LIMIT = 20;

function readRecent(): string[] {
  try {
    const raw = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    return Array.isArray(raw) ? raw.filter((s): s is string => typeof s === 'string').slice(0, MAX_RECENT) : [];
  } catch {
    return [];
  }
}

function parse(text: string, ctx: SearchParseContext | undefined, now: Date): ParsedQuery {
  return ctx ? parseQuery(text, { ...ctx, now }) : { q: text, filters: {} };
}

/** A reading is for this exact text on this local day ("last month" moves at midnight). */
const readingKey = (text: string, today: string) => `${today}\n${text.trim()}`;
const wordCount = (s: string) => s.trim().split(/\s+/).filter(Boolean).length;
/** The user has chip edits in play (after pruning). */
const hasEdits = (o: FilterOverrides) => o.removed.length > 0 || Boolean(o.entity || o.date || o.dateField);

const dateKeyOf = (d: ChipFilters['date']) => (d ? `date:${d.from}/${d.to}` : null);

/**
 * When an edit takes over from a reading: an edit that replaced one of the
 * reading's values now replaces what the parser has there, so it survives.
 */
function rebaseOffReading(o: FilterOverrides, reading: MergedQuery, parsed: ChipFilters): FilterOverrides {
  let next = o;
  if (o.entity && o.entity.replaces && reading.aiKeys.includes(`entity:${o.entity.replaces}`)) {
    next = { ...next, entity: { ...o.entity, replaces: parsed.entity ?? null } };
  }
  if (o.date && o.date.replaces && reading.aiKeys.includes(o.date.replaces)) {
    next = { ...next, date: { ...o.date, replaces: dateKeyOf(parsed.date) } };
  }
  return next;
}

/**
 * Universal search state (P4, spec §3.5): 100 ms debounce; stale-while-typing
 * (results from the last completed query stay on screen until newer ones
 * arrive, so the list never blanks per keystroke); superseded requests are
 * aborted and their late answers ignored; recent searches kept per browser
 * (a convenience only — storage may be unavailable).
 *
 * Contextual queries (T-148 P4b-1c): with a parse context, the text is parsed
 * on every keystroke (query.v1). Understood phrases become filters shown as
 * chips; the chip edits live in `overrides`; the rest of the text is `q`.
 *
 * AI understanding (T-151, understand.v1): when the parser leaves 2+ words it
 * couldn't read, the text is sent for an AI reading after a 400 ms pause (or at
 * once via `understandNow`, the Enter key). The reading only fills filters the
 * parser left empty (understandMerge.ts); its chips carry `ai`. Readings are
 * kept per text, so they apply only to the exact text they were made for.
 * `dismissReading` drops the reading for the current text: its words can't be
 * put back one chip at a time, so the whole reading goes.
 *
 * Edits and readings don't mix. The chip edits are recorded against what the
 * user sees, and a reading comes and goes with the text, so layering one on the
 * other can silently revert an edit or bring back an AI value. Instead: an edit
 * made while a reading shows or is on its way takes over from it (the reading
 * is dropped for that text, edits that replaced its values are rebased onto the
 * parser's), and while the user has any edit, no reading is asked for or
 * applied. Readings resume once the edits lapse.
 */
export function useUniversalSearch(parseCtx?: SearchParseContext, { sales = false }: { sales?: boolean } = {}) {
  const [query, setQueryState] = useState('');
  const [overrides, setOverrides] = useState<FilterOverrides>(EMPTY_OVERRIDES);
  // "Today" for relative dates, refreshed on every edit (an event, not render).
  const [now, setNow] = useState(() => new Date());
  const [appGroups, setAppGroups] = useState<SearchV1Group[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [salesUnavailable, setSalesUnavailable] = useState(false);
  // ISS-135: Sales' results come from their own request (staff only), so App's
  // show at once and Sales' section is appended when it lands.
  const [salesGroups, setSalesGroups] = useState<SearchV1Group[]>([]);
  const [salesPending, setSalesPending] = useState(false);
  const groups = useMemo(() => [...appGroups, ...salesGroups], [appGroups, salesGroups]);
  const [recent, setRecent] = useState<string[]>(readRecent);
  const controllerRef = useRef<AbortController | null>(null);
  const seqRef = useRef(0);
  // AI readings by readingKey (null = the server said "use your parser").
  const [readings, setReadings] = useState<Record<string, UnderstandV1 | null>>({});
  // The same readings, mirrored synchronously. A reading's request clears at once
  // but its state lands on the next render, so an event handler fired in between
  // (a click from the last render) must read this, or its edit won't take over.
  const readingsRef = useRef<Record<string, UnderstandV1 | null>>({});
  const storeReadings = useCallback((update: (prev: Record<string, UnderstandV1 | null>) => Record<string, UnderstandV1 | null>) => {
    readingsRef.current = update(readingsRef.current);
    setReadings(readingsRef.current);
  }, []);
  const [dismissed, setDismissed] = useState<readonly string[]>([]);
  const [askNowKey, setAskNowKey] = useState<string | null>(null);
  const [understanding, setUnderstanding] = useState(false);
  // The request (text + filters) whose App answer is on screen. Until the current
  // request's answer lands, the empty list is not its answer ("Searching…", as Sales).
  const [answeredKey, setAnsweredKey] = useState<string | null>(null);
  const understandRef = useRef<{ key: string; controller: AbortController } | null>(null);
  // Bumped when a reading lands for the text on screen, to re-run the search.
  const [searchNonce, setSearchNonce] = useState(0);

  const timeZone = parseCtx?.timeZone ?? 'UTC';
  const today = localToday(now, timeZone);
  const key = readingKey(query, today);

  /** The parse of `text`, merged with its AI reading, unless dismissed or the user has edits. */
  const read = useCallback((
    text: string, at: Date, dismissedKeys: readonly string[], o: FilterOverrides,
  ): MergedQuery => {
    const k = readingKey(text, localToday(at, timeZone));
    const body = hasEdits(o) || dismissedKeys.includes(k) ? null : readings[k] ?? null;
    return mergeUnderstanding(parse(text, parseCtx, at), body);
  }, [parseCtx, readings, timeZone]);

  const parsed = useMemo(() => parse(query, parseCtx, now), [query, parseCtx, now]);
  const merged = useMemo(() => read(query, now, dismissed, overrides), [read, query, now, dismissed, overrides]);
  const filters = useMemo(() => effectiveFilters(merged.filters, overrides), [merged, overrides]);
  const chips: Chip[] = useMemo(() => buildChips(filters, {
    companies: [
      ...(parseCtx?.companies ?? []),
      ...Object.entries(merged.labels).map(([id, name]) => ({ id, name })),
    ],
    models: parseCtx?.models ?? [],
    now,
    timeZone,
  }, merged.aiKeys), [filters, parseCtx, merged, now, timeZone]);

  const q = merged.q.trim();
  const active = Boolean(q) || hasFilters(filters);
  const params = useMemo(() => toSearchParams(filters, timeZone), [filters, timeZone]);
  const paramsKey = JSON.stringify(params);
  // The request on screen (text + filters), as one string.
  const requestKey = JSON.stringify([q, params]);
  // What's on screen, for async callbacks (a reading landing) to compare against.
  const latestRef = useRef({ key, overrides, dismissed, requestKey, now, parseCtx });
  useEffect(() => { latestRef.current = { key, overrides, dismissed, requestKey, now, parseCtx }; });

  // Every edit goes through here (events only). An edit that changes the
  // request supersedes the one in flight AT ONCE, not when the next debounce
  // fires, so a late answer can't show under the new text. An edit that leaves
  // nothing to search also drops the results.
  const commit = useCallback((text: string, nextOverrides: FilterOverrides, dismissedKeys = dismissed) => {
    const at = new Date();
    let next = read(text, at, dismissedKeys, nextOverrides);
    const pruned = pruneOverrides(nextOverrides, next.filters);
    // Once every edit has lapsed, a reading may apply again, as the next render will see it.
    if (pruned !== nextOverrides) next = read(text, at, dismissedKeys, pruned);
    const nextFilters = effectiveFilters(next.filters, pruned);
    setQueryState(text);
    setOverrides(pruned);
    setNow(at);
    // A reading still on its way for other text is no longer wanted.
    const inFlight = understandRef.current;
    if (inFlight && inFlight.key !== readingKey(text, localToday(at, timeZone))) {
      inFlight.controller.abort();
      understandRef.current = null;
      setUnderstanding(false);
    }
    const nextKey = JSON.stringify([next.q.trim(), toSearchParams(nextFilters, timeZone)]);
    if (nextKey !== JSON.stringify([q, params])) {
      controllerRef.current?.abort();
      seqRef.current += 1; // any late answer is now superseded
    }
    if (!next.q.trim() && !hasFilters(nextFilters)) {
      setAppGroups([]);
      setSalesGroups([]);
      // The answer went with its results: the same text typed again is not
      // answered until it searches again ("Searching…", not "No matches").
      setAnsweredKey(null);
      setLoading(false);
      setSalesPending(false);
      setSalesUnavailable(false);
      setError(null);
    }
  }, [read, dismissed, timeZone, q, params]);

  const setQuery = useCallback((value: string) => commit(value, overrides), [commit, overrides]);

  /** Re-read "today" (the dialog reopening in a tab left open across midnight). */
  const refreshClock = useCallback(() => commit(query, overrides), [commit, query, overrides]);

  /** Drop the reading for the current text (shown or on its way), then commit `nextOverrides`. */
  const takeOverFromReading = useCallback((nextOverrides: FilterOverrides) => {
    const inFlight = understandRef.current;
    if (inFlight?.key === key) {
      inFlight.controller.abort();
      understandRef.current = null;
      setUnderstanding(false);
    }
    // Dismissals never age out (a few short strings per open search): once the
    // user has taken over from a text's reading, it is never asked for again.
    const next = dismissed.includes(key) ? dismissed : [...dismissed, key];
    setDismissed(next);
    // Forget the reading itself too, so it can't resurface once `dismissed` ages out.
    storeReadings((prev) => {
      if (!(key in prev)) return prev;
      const rest = { ...prev };
      delete rest[key];
      return rest;
    });
    commit(query, rebaseOffReading(nextOverrides, merged, parsed.filters), next);
  }, [commit, dismissed, key, merged, parsed, query, storeReadings]);

  /**
   * Apply a chip edit (remove, pick a range, choose an entity or date field).
   * Made while a reading shows or is on its way, the edit takes over from it.
   */
  const editFilters = useCallback((edit: (o: FilterOverrides, parsedFilters: ChipFilters) => FilterOverrides) => {
    const edited = edit(overrides, merged.filters);
    const readingInPlay = !dismissed.includes(key)
      && (Boolean(readingsRef.current[key]) || understandRef.current?.key === key);
    if (readingInPlay) takeOverFromReading(edited);
    else commit(query, edited);
  }, [commit, dismissed, key, merged, overrides, query, takeOverFromReading]);

  /** Ask for an AI reading of the current text now (Enter), not after the pause: once, and never under edits. */
  const understandNow = useCallback(() => {
    // Enter is an event: read the clock, so a text typed before midnight is asked about today.
    const at = new Date();
    const k = readingKey(query, localToday(at, timeZone));
    if (k in readingsRef.current || dismissed.includes(k) || understandRef.current?.key === k) return;
    if (hasEdits(overrides) || wordCount(parse(query, parseCtx, at).q) < 1) return;
    if (k !== key) commit(query, overrides); // a new day: re-read "today" first
    setAskNowKey(k);
  }, [commit, dismissed, key, overrides, parseCtx, query, timeZone]);

  /** Drop the AI reading for the current text: the parser's chips and words come back, and it isn't asked again. */
  const dismissReading = useCallback(() => takeOverFromReading(overrides), [overrides, takeOverFromReading]);

  // T-151: ask for an AI reading when the parser leaves 2+ words (or on Enter,
  // 1+), once per text. Never without a parse context (no parser to merge
  // with), and never while the user has chip edits (edits and readings don't mix).
  const readingDue = useMemo(() => {
    const text = query.trim();
    if (!parseCtx || !text || text.length > MAX_UNDERSTAND_QUERY || hasEdits(overrides)) return false;
    if (key in readings || dismissed.includes(key)) return false;
    return wordCount(parsed.q) >= (askNowKey === key ? 1 : 2);
  }, [parseCtx, query, overrides, key, readings, dismissed, parsed.q, askNowKey]);

  useEffect(() => {
    if (!readingDue || understandRef.current?.key === key) return undefined;
    const text = query.trim();
    const asked = askNowKey === key;
    const timer = setTimeout(() => {
      understandRef.current?.controller.abort();
      const controller = new AbortController();
      understandRef.current = { key, controller };
      // Enter asks for this one request; later visits to the text follow the normal rule.
      if (asked) setAskNowKey(null);
      setUnderstanding(true);
      const keep = (body: UnderstandV1 | null) => storeReadings((prev) => {
        const next = { ...prev, [key]: body };
        const keys = Object.keys(next);
        for (const old of keys.slice(0, Math.max(0, keys.length - MAX_READINGS))) delete next[old];
        return next;
      });
      // Never answered in time: keep the parser's reading for this text (null), as the
      // server does when the AI is off or slow. It doesn't wait for the call to fail.
      const giveUp = setTimeout(() => {
        if (understandRef.current?.controller !== controller) return; // the text moved on
        controller.abort();
        keep(null);
        understandRef.current = null;
        setUnderstanding(false);
      }, UNDERSTAND_CLIENT_TIMEOUT_MS);
      understandSearch(text, { tz: timeZone, today, signal: controller.signal })
        .then((body) => {
          if (controller.signal.aborted) return;
          const shown = latestRef.current;
          if (body && shown.key === key && !hasEdits(shown.overrides) && !shown.dismissed.includes(key)) {
            // Does the reading change what the text on screen searches for? Only then
            // is the search in flight for an old request: supersede it now, before its
            // answer can land in the same tick, and search again. A reading that adds
            // nothing leaves that search (or its answer) standing (Sentry review).
            const m = mergeUnderstanding(parse(query, shown.parseCtx, shown.now), body);
            const next = JSON.stringify([m.q.trim(), toSearchParams(effectiveFilters(m.filters, shown.overrides), timeZone)]);
            if (next !== shown.requestKey) {
              controllerRef.current?.abort();
              seqRef.current += 1;
              setSearchNonce((n) => n + 1);
            }
          }
          keep(body);
        })
        .catch(() => { /* aborted: the text moved on, or given up above */ })
        .finally(() => {
          clearTimeout(giveUp);
          if (understandRef.current?.controller === controller) {
            understandRef.current = null;
            setUnderstanding(false);
          }
        });
    }, asked ? 0 : UNDERSTAND_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [readingDue, query, key, askNowKey, timeZone, today, storeReadings]);

  // A request that changes without an edit (a reading landing) supersedes the
  // search in flight at once, as commit() does for edits: its late answer is
  // for the old request and must not show under the new chips.
  const lastRequestKeyRef = useRef(requestKey);
  useEffect(() => {
    if (lastRequestKeyRef.current === requestKey) return;
    lastRequestKeyRef.current = requestKey;
    controllerRef.current?.abort();
    seqRef.current += 1;
  }, [requestKey]);

  useEffect(() => {
    if (!active) return undefined;
    const timer = setTimeout(() => {
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      const seq = ++seqRef.current;
      // The same string as requestKey, for the request this search answers.
      const searchKey = JSON.stringify([q, JSON.parse(paramsKey)]);
      setLoading(true);
      setError(null); // a new search replaces the previous failure
      setSalesUnavailable(false); // …and any previous "Sales unavailable" note
      setSalesPending(sales);
      const filterParams = JSON.parse(paramsKey) as [string, string][];
      const opts = {
        signal: controller.signal,
        ...(filterParams.length ? { filters: filterParams } : {}),
        ...(filterParams.some(([k]) => k === 'entity') ? { limit: ENTITY_LIMIT } : {}),
      };
      // ISS-135: App's and Sales' results come back separately, so App's never
      // wait on the slower Sales leg. Sales' are appended after App's. A Sales
      // answer that beats this search's App answer is held until App's settles,
      // so it never sits beside the previous search's App results (Codex, #965).
      let appSettled = false;
      let heldSales: (() => void) | null = null;
      const appDone = () => {
        appSettled = true;
        heldSales?.();
        heldSales = null;
      };
      const showSales = (apply: () => void) => {
        if (appSettled) apply();
        else heldSales = apply;
      };
      searchV1(q, opts)
        .then((env) => {
          if (seq !== seqRef.current) return; // superseded
          setAppGroups(env.groups);
          setSalesGroups([]); // the previous search's; this one's follows
          setError(null);
          setAnsweredKey(searchKey);
          appDone();
        })
        .catch((err: unknown) => {
          if (seq !== seqRef.current || (err instanceof DOMException && err.name === 'AbortError')) return;
          // The previous search's results are not this search's.
          setAppGroups([]);
          setSalesGroups([]);
          setError('Search failed');
          setAnsweredKey(searchKey);
          appDone();
        })
        .finally(() => {
          if (seq === seqRef.current) setLoading(false);
        });
      if (!sales) {
        setSalesGroups([]);
      } else {
        searchV1(q, { ...opts, salesOnly: true })
          .then((env) => {
            if (seq !== seqRef.current) return;
            showSales(() => {
              setSalesGroups(env.groups);
              setSalesUnavailable(env.federation?.sales === 'unavailable');
              setSalesPending(false);
            });
          })
          .catch((err: unknown) => {
            if (seq !== seqRef.current || (err instanceof DOMException && err.name === 'AbortError')) return;
            // Sales couldn't be searched: say so, never "no Sales results".
            showSales(() => {
              setSalesGroups([]);
              setSalesUnavailable(true);
              setSalesPending(false);
            });
          });
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [active, q, paramsKey, searchNonce, sales]);

  useEffect(() => () => {
    controllerRef.current?.abort();
    understandRef.current?.controller.abort();
  }, []);

  const rememberQuery = useCallback((raw: string) => {
    const text = raw.trim();
    if (!text) return;
    setRecent((prev) => {
      const next = [text, ...prev.filter((p) => p.toLowerCase() !== text.toLowerCase())].slice(0, MAX_RECENT);
      try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch { /* storage blocked: keep in memory */ }
      return next;
    });
  }, []);

  return {
    query, setQuery, groups, loading, error, recent, rememberQuery,
    /** Sales was asked but couldn't be searched: App's results are still shown. */
    salesUnavailable,
    /** ISS-135: Sales' results are still on their way (App's may already show). */
    salesPending,
    /** True when there is something to search: text left over, or at least one filter. */
    active, filters, chips, now, timeZone, editFilters, refreshClock,
    /** The App answer for the text and filters on screen has arrived (Sales' `answered`). */
    answered: answeredKey === requestKey,
    /** T-151: an AI reading is due or on its way (ISS-209: not "No matches" yet); ask one now (Enter); drop the current one. */
    understanding: understanding || readingDue, understandNow, dismissReading,
  };
}
